<template>
  <div class="row">
    <div v-if="isLoading" class="col-md-12 text-right">
      <app-spinner/>
    </div>
    <template v-else>
      <div class="col-md-6">
        <el-button type="primary"
                   :disabled="isDiscountApplied || isNotInvoiced"
                   @click="handleCalculateRefundFull">
          Calculate full refund
        </el-button>
        <el-button type="info"
                   :disabled="isInvalidProductsOrShipping || isNotInvoiced"
                   @click="handleCalculateRefundPartial">
          Calculate partial refund
        </el-button>
        <p v-if="$v.products.$each.$invalid" class="text-danger">
          Some of the refund line item(s) are invalid. Please review selection.
        </p>
        <p v-if="$v.shipping.$invalid" class="text-danger">
          The shipping refund amount is invalid.
        </p>
      </div>
      <div class="col-md-6 text-right">
        <el-checkbox v-model="warehouseModel"
                     :value="warehouse"
                     :disabled="hasExchange"
                     label="Return to warehouse"
                     border/>
        <el-button type="success"
                   :disabled="disabled"
                   @click="handleCreateDraftRA">
          Create Draft RA
        </el-button>
        <p v-if="!$v.raRemarks.required" class="text-danger">
          Please enter return authorization remarks.
        </p>
        <p v-if="!$v.reasonCode.required" class="text-danger">
          Please select a reason code.
        </p>
      </div>
    </template>
    <el-dialog :visible.sync="isWarehouseAlertVisible" width="30%">
      <template slot="title">
        <h4>Wait, does this return need to go back to the warehouse?</h4>
      </template>
      <span slot="footer" class="dialog-footer">
        <el-button class="m-b-1" @click="handleConfirmationBoxYes">
          No
        </el-button>
        <el-button type="primary" @click="handleConfirmationBoxNo">
          Yes
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  import {mapMutations, mapState, mapActions} from 'vuex';
  import {
    ORDER_RETURN_FULL_REFUND,
    ORDER_RETURN_REFUND,
    ORDER_RETURN_SET_WAREHOUSE,
    ORDER_RETURN_CREATE_DRAFT_RA,
    ORDER_RETURN_DISCOUNT_PRICE_RULE_ADD,
    ORDER_RETURN_SET_LOADING,
  } from '../../../store/modules/order/return/types';
  import {
    D365_STATUS_INVOICED,
    WAREHOUSE_RETURN_MAXIMUM_AMOUNT,
  } from '../../../lang/en/constants';
  import AppSpinner from '../../ui/AppSpinner';

  export default {
    name: 'OrderReturnControls',
    inject: ['$v'],
    components: {
      AppSpinner,
    },
    computed: {
      ...mapState({
        warehouse: state => state.orderReturn.warehouse,
        isLoading: state => state.orderReturn.loading,
        isDiscountApplied: state => state.orderReturn.discount.price_rule !==
            null,
        isNotInvoiced: state => state.orderReturn.d365Order.header.SalesOrderProcessingStatus !== D365_STATUS_INVOICED,
        draftRA: state => state.orderReturn.draftRA,
        products: state => state.orderReturn.products,
        discount: state => state.orderReturn.discount,
      }),
      disabled() {
        return this.$v.$anyDirty || this.$v.$invalid || this.isNotInvoiced;
      },
      isInvalidProductsOrShipping() {
        return this.$v.products.$invalid || this.$v.shipping.$invalid;
      },
      warehouseModel: {
        get() {
          return this.warehouse;
        },
        set(value) {
          this.setWarehouse(value);
        },
      },
      hasExchange() {
        const hasExchange = this.products.reduce((sum, product) => {
          if (product.exchange) {
            sum += product.returnItem.quantity;
          }

          return sum;
        }, 0) > 0;

        if (hasExchange) {
          this.setWarehouse(true);
        }

        return hasExchange;
      },
    },
    methods: {
      ...mapMutations({
        setWarehouse: ORDER_RETURN_SET_WAREHOUSE,
      }),
      ...mapActions({
        calculatePartialRefund: ORDER_RETURN_REFUND,
        calculateFullRefund: ORDER_RETURN_FULL_REFUND,
        createDraftRA: ORDER_RETURN_CREATE_DRAFT_RA,
        addDiscount: ORDER_RETURN_DISCOUNT_PRICE_RULE_ADD,
        setLoading: ORDER_RETURN_SET_LOADING,
      }),
      async handleCreateDraftRA() {
        // discount is applied or is not present, proceed as normal
        if (this.isDiscountApplied || !this.discount.value) {
            await this.checkReturnToWarehouseStatus();
            return;
        }

        // prompt the user to apply their discount;
        // ignore the error on cancel and proceed to next step
        try {
          await this.$confirm('A discount amount is present but not applied; would you like to apply it?', 'Warning', {
              confirmButtonText: 'Apply',
              cancelButtonText: 'Do not apply',
              type: 'warning'
          });
          await this.addDiscount();
        } catch {
            // ignore error
        } finally {
          await this.checkReturnToWarehouseStatus();
        }
      },
      async checkReturnToWarehouseStatus() {
        const {total_price} = this.draftRA;

        // If the total value of refund is greater than 100 and “Return to WH” box is NOT ticked,
        // Warning Pop UP Box – “Are you sure this is not returning to warehouse” –
        // clicking Yes I am sure closes warning box,
        // clicking No I am not sure closes warning box and ticks “return to warehouse”
        if (Number(total_price) > WAREHOUSE_RETURN_MAXIMUM_AMOUNT &&
            !this.warehouse) {
          this.isWarehouseAlertVisible = true;
        } else {
          await this.createReturnAuthorization();
        }
      },
      async createReturnAuthorization() {
        await this.createDraftRA().then(() => {
          this.$router.push({path: 'confirm'});
        });
      },
      handleCalculateRefundPartial() {
        return this.calculatePartialRefund().then(() => {
          this.$v.$reset();
        });
      },
      handleCalculateRefundFull() {
        this.calculateFullRefund().then(() => {
          this.$v.$reset();
        });
      },
      handleConfirmationBoxYes() {
        this.isWarehouseAlertVisible = false;
        this.createReturnAuthorization();
      },
      handleConfirmationBoxNo() {
        this.isWarehouseAlertVisible = false;
        this.warehouseModel = true;
      },
    },
    data() {
      return {
        isWarehouseAlertVisible: false,
      };
    },
  };
</script>
